/**
 * The design system.
 *
 * Small and deliberately unambitious: a button, a badge, a table, a drawer, an empty
 * state and a spinner. Everything else is composed from them in a feature.
 *
 * `ConfidenceBadge` and `ProvenanceLink` live in their own files because they are not
 * generic — they encode ADR-0016 and the provenance chain, and a reviewer looking for
 * "where does the product show confidence" should find one file, not a variant of
 * `Badge`.
 */

import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';

export { ConfidenceBadge } from './ConfidenceBadge';
export { ProvenanceLink } from './ProvenanceLink';
export { QrCode } from './QrCode';
export { CopyButton } from './CopyButton';

type ButtonTone = 'primary' | 'secondary' | 'danger' | 'ghost';

const BUTTON_CLASS: Record<ButtonTone, string> = {
  primary: 'bg-accent text-on-accent hover:opacity-90',
  secondary: 'border border-line bg-raised text-ink hover:bg-surface',
  danger: 'border border-danger/50 bg-danger/10 text-danger hover:bg-danger/20',
  ghost: 'text-muted hover:text-ink',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual weight. */
  tone?: ButtonTone;
}

/** A button. */
export function Button({ tone = 'secondary', className = '', ...rest }: ButtonProps): ReactElement {
  return (
    <button
      {...rest}
      className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${BUTTON_CLASS[tone]} ${className}`}
    />
  );
}

export interface BadgeProps {
  /** What to show. */
  children: ReactNode;
  /** Semantic role, never a colour. */
  tone?: 'ok' | 'warn' | 'danger' | 'muted' | 'accent';
}

const BADGE_CLASS: Record<string, string> = {
  ok: 'border-ok/40 bg-ok/10 text-ok',
  warn: 'border-warn/40 bg-warn/10 text-warn',
  danger: 'border-danger/40 bg-danger/10 text-danger',
  muted: 'border-line bg-raised text-muted',
  accent: 'border-accent/40 bg-accent/10 text-accent',
};

/** A small status pill. */
export function Badge({ children, tone = 'muted' }: BadgeProps): ReactElement {
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium ${BADGE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

export interface CardProps {
  /** Heading. */
  title?: ReactNode;
  /** Optional action area. */
  action?: ReactNode;
  /** Body. */
  children: ReactNode;
}

/** A titled panel. */
export function Card({ title, action, children }: CardProps): ReactElement {
  return (
    <section className="rounded-lg border border-line bg-raised">
      {(title || action) && (
        <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
          <h2 className="text-sm font-semibold">{title}</h2>
          {action}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export interface TableProps {
  /** Column headings. */
  headers: ReactNode[];
  /** Row cells. */
  rows: ReactNode[][];
  /** What to show when there are no rows. */
  empty?: ReactNode;
}

/** A table with a horizontal scroll container, so a wide row never scrolls the page. */
export function Table({ headers, rows, empty = 'Nothing to show.' }: TableProps): ReactElement {
  if (rows.length === 0) return <EmptyState>{empty}</EmptyState>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
            {headers.map((header, index) => (
              <th key={index} scope="col" className="px-3 py-2 font-medium">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-line/60 last:border-0">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A neutral "nothing here" panel. */
export function EmptyState({ children }: { children: ReactNode }): ReactElement {
  return <p className="rounded border border-dashed border-line px-4 py-6 text-center text-sm text-muted">{children}</p>;
}

/** An error panel. Says what failed, never just that something did. */
export function ErrorState({ error }: { error: unknown }): ReactElement {
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  return (
    <p role="alert" className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
      {message}
    </p>
  );
}

/** A loading indicator with an accessible label. */
export function Loading({ label = 'Loading' }: { label?: string }): ReactElement {
  return (
    <p className="px-4 py-6 text-center text-sm text-muted" role="status">
      {label}…
    </p>
  );
}

export interface DrawerProps {
  /** Whether the drawer is showing. */
  open: boolean;
  /** Heading. */
  title: ReactNode;
  /** Close handler. */
  onClose: () => void;
  /** Body. */
  children: ReactNode;
}

/** A right-hand drawer, used for evidence drill-down. */
export function Drawer({ open, title, onClose, children }: DrawerProps): ReactElement | null {
  if (!open) return null;
  return (
    <aside
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : 'Details'}
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col border-l border-line bg-raised shadow-xl"
    >
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        <Button tone="ghost" onClick={onClose} aria-label="Close">
          ✕
        </Button>
      </header>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </aside>
  );
}
